
import { CheckCircle2, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "@/hooks/useTranslation";

type VerificationStatus = 'none' | 'pending' | 'verified' | 'rejected' | 'verified_creative';

interface VerificationBadgeProps {
  status: VerificationStatus;
  isCreative?: boolean;
  className?: string;
  showText?: boolean;
}

export function VerificationBadge({ 
  status, 
  isCreative = false, 
  className = "",
  showText = false 
}: VerificationBadgeProps) {
  const { t } = useTranslation();
  
  if (status === 'none' || status === 'pending' || status === 'rejected') return null;

  // If the user is verified and is a creative, show the creative badge
  const effectiveStatus = (status === 'verified' && isCreative) ? 'verified_creative' : status;

  const badges = {
    verified: {
      icon: <CheckCircle2 className={`${showText ? 'h-3 w-3' : 'h-4 w-4'} ${!showText ? 'text-[#0EA5E9]' : ''}`} />,
      text: t('verified_traveler'),
      tooltip: t('verified_traveler_tooltip'),
      className: "bg-[#0EA5E9] hover:bg-[#0284C7]"
    },
    verified_creative: {
      icon: <Star className={`${showText ? 'h-3 w-3' : 'h-4 w-4'} ${!showText ? 'text-[#818CF8]' : ''}`} />,
      text: t('verified_creative'),
      tooltip: t('verified_creative_tooltip'),
      className: "bg-[#818CF8] hover:bg-[#6366F1]"
    }
  }[effectiveStatus];

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          {showText ? (
            <Badge 
              variant="default"
              className={`gap-1 ${badges.className} ${className}`}
            >
              {badges.icon}
              {badges.text}
            </Badge>
          ) : (
            badges.icon
          )}
        </TooltipTrigger>
        <TooltipContent>
          <p>{badges.tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
